import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import TopExperienceLayer from '../components/top/TopExperienceLayer';
import Footer from '../components/layout/Footer';
import {
  GraduationCap, Search, Briefcase, Bell, Zap, Trophy,
  CheckCircle2, Lock, ArrowRight, Heart, Users, Mail
} from 'lucide-react';

const PRIMARY   = '#1a5d3a';
const GOLD      = '#f5a623';
const _GOLD_HOV  = '#e09000';
const CREAM     = '#f5f0e8';
const SAGE      = '#e8f5e9';
const WHITE     = '#FFFFFF';
const WARM_CARD = '#fff8e7';

const PILLARS = [
  { icon: GraduationCap, label: 'COLLEGE INSIGHTS',        desc: 'Admissions tips, campus highlights & expert advice.' },
  { icon: Search,        label: 'SCHOLARSHIP ALERTS',      desc: 'Find and win scholarships that fit you.' },
  { icon: Briefcase,     label: 'CAREER GUIDANCE',         desc: 'Explore paths, industries & future opportunities.' },
  { icon: Bell,          label: 'DEADLINES & REMINDERS',   desc: "Never miss what matters. We've got your back." },
  { icon: Zap,           label: 'EXCLUSIVE OPPORTUNITIES', desc: "Internships, programs & events you won't find anywhere else." },
  { icon: Trophy,        label: 'LEADERS WHO INSPIRE',     desc: 'Stories of teens making impact and leading change.' },
];

const RECENT_ISSUES = [
  { date: 'MAY 9, 2025',   number: '#78', headline: 'Top Summer Programs to Boost Your College Profile' },
  { date: 'MAY 2, 2025',   number: '#77', headline: "Scholarships You Shouldn't Miss This May" },
  { date: 'APR 25, 2025',  number: '#76', headline: 'Careers in AI: What High School Students Should Know' },
];

export default function NewsletterPage() {
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (email) setSubscribed(true);
  };

  return (
    <div style={{ background: WHITE, minHeight: '100vh' }}>
      <TopExperienceLayer />

      {/* ── HERO ── */}
      <section style={{ background: PRIMARY, padding: '72px 0 64px' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left */}
            <div>
              {/* OK branding */}
              <div className="flex items-center gap-3 mb-6">
                <div
                  className="flex items-center justify-center font-black text-white"
                  style={{
                    background: 'rgba(255,255,255,0.15)', borderRadius: '6px',
                    border: '1px solid rgba(255,255,255,0.3)',
                    width: 48, height: 48, fontSize: '1.1rem', letterSpacing: '-0.04em'
                  }}
                >OK</div>
                <div>
                  <div className="font-black text-white text-sm tracking-wide">NEWSLETTER</div>
                  <div className="text-xs font-bold tracking-widest uppercase text-white/50">BY STUDENTSIGNAL</div>
                </div>
              </div>

              <h1
                className="font-black leading-none text-white"
                style={{ fontSize: 'clamp(3rem, 8vw, 5.5rem)', letterSpacing: '-0.03em', lineHeight: 0.9 }}
              >
                OPPORTUNITY
              </h1>
              <h1
                className="font-black leading-none mb-5"
                style={{ color: GOLD, fontSize: 'clamp(3rem, 8vw, 5.5rem)', letterSpacing: '-0.03em', lineHeight: 0.9 }}
              >
                KNOCKS
              </h1>

              <p className="font-semibold mb-2 text-white/90" style={{ fontSize: '1.1rem' }}>
                Your weekly signal for <span style={{ color: GOLD }}>what&apos;s next.</span>
              </p>
              <div className="flex items-center gap-2 mb-6">
                <CheckCircle2 size={14} style={{ color: GOLD }} />
                <span className="text-sm text-white/70">For high school students (10th–12th grade)</span>
              </div>

              {/* Email capture */}
              {subscribed ? (
                <div className="flex items-center gap-3 p-4 rounded-lg" style={{ background: 'rgba(255,255,255,0.15)' }}>
                  <CheckCircle2 size={20} className="text-white" />
                  <span className="font-bold text-white">You&apos;re in! Check your inbox for your first issue.</span>
                </div>
              ) : (
                <form onSubmit={handleSubscribe} className="flex gap-2 mb-5 max-w-md">
                  <div className="flex-1 flex items-center gap-2 px-4 rounded-lg bg-white">
                    <Mail size={16} style={{ color: '#aaa' }} />
                    <input
                      type="email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      placeholder="Enter your email address"
                      className="flex-1 py-3 text-sm bg-transparent outline-none"
                      style={{ color: '#111' }}
                    />
                  </div>
                  <button
                    type="submit"
                    className="px-5 py-3 font-black text-sm whitespace-nowrap transition-all hover:opacity-90 rounded-lg"
                    style={{ background: GOLD, color: WHITE, letterSpacing: '0.02em' }}
                  >
                    GET YOUR FREE ISSUE
                  </button>
                </form>
              )}

              {/* Trust strip */}
              <div className="flex flex-wrap gap-5">
                {['100% Free', 'Weekly in Your Inbox', 'Unsubscribe Anytime'].map((t) => (
                  <div key={t} className="flex items-center gap-1.5">
                    <CheckCircle2 size={14} style={{ color: GOLD }} />
                    <span className="text-xs font-medium text-white/70">{t}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right — newsletter cover mockup */}
            <div className="hidden lg:flex items-center justify-center">
              <div
                className="relative overflow-hidden"
                style={{
                  width: 260, height: 340, borderRadius: '8px',
                  background: WARM_CARD,
                  boxShadow: '0 30px 60px rgba(0,0,0,0.25)',
                  border: '1px solid rgba(255,255,255,0.3)',
                }}
              >
                <div className="p-4" style={{ background: PRIMARY }}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 flex items-center justify-center font-black text-white text-xs"
                        style={{ background: 'rgba(255,255,255,0.2)', borderRadius: '4px', border: '1px solid rgba(255,255,255,0.3)' }}>OK</div>
                      <span className="text-white font-bold text-xs">NEWSLETTER</span>
                    </div>
                    <span className="text-xs text-white/60">ISSUE #70</span>
                  </div>
                  <div className="font-black text-white text-xl leading-tight mt-3">
                    Big opportunities start with small steps.
                  </div>
                  <div className="text-xs mt-2 text-white/60">This week&apos;s top picks curated for your future.</div>
                </div>
                {/* Placeholder cover image area */}
                <div className="bg-gray-200 rounded-lg w-full h-full min-h-48" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── 6 PILLARS ── */}
      <section className="py-16" style={{ background: WHITE }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="text-xs font-bold tracking-widest uppercase mb-2" style={{ color: PRIMARY }}>
            WHAT YOU&apos;LL GET EVERY WEEK
          </div>
          <h2 className="text-3xl font-black mb-12" style={{ color: '#111', letterSpacing: '-0.02em' }}>
            6 Pillars. Endless Possibilities.
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {PILLARS.map(({ icon: Icon, label, desc }) => (
              <div key={label} className="text-center">
                <div className="flex justify-center mb-3">
                  <div className="w-16 h-16 flex items-center justify-center" style={{ background: SAGE, borderRadius: '50%' }}>
                    <Icon size={28} style={{ color: PRIMARY }} strokeWidth={1.5} />
                  </div>
                </div>
                <div className="font-black text-xs tracking-wide mb-2" style={{ color: '#111', letterSpacing: '0.04em' }}>{label}</div>
                <p className="text-xs leading-relaxed" style={{ color: '#777' }}>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SAMPLE ISSUE BLUR GATE ── */}
      <section className="py-16" style={{ background: CREAM }}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-2xl overflow-hidden" style={{ background: WARM_CARD, border: '1px solid #e8d8b0' }}>
            {/* Issue header */}
            <div className="p-6" style={{ borderBottom: '1px solid #e8d8b0', background: PRIMARY }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center font-black text-white text-xs"
                    style={{ background: 'rgba(255,255,255,0.2)', borderRadius: '4px', border: '1px solid rgba(255,255,255,0.3)' }}>OK</div>
                  <span className="font-black text-white text-sm">NEWSLETTER</span>
                </div>
                <span className="text-xs text-white/60">ISSUE #78 · MAY 9, 2025</span>
              </div>
              <div className="mt-4">
                <div className="font-black text-white text-lg leading-tight mb-1">
                  Handpicked opportunities. Real impact. Built for students like you.
                </div>
                <span className="inline-block mt-2 px-3 py-1 text-xs font-bold text-white rounded"
                  style={{ background: GOLD }}>
                  THIS WEEK&apos;S SPOTLIGHT
                </span>
              </div>
            </div>

            {/* Blurred content */}
            <div className="relative">
              <div style={{ filter: 'blur(8px)', userSelect: 'none', opacity: 0.3, padding: '24px' }}>
                <div className="grid grid-cols-2 gap-4">
                  {[1, 2, 3, 4].map(i => (
                    <div key={i} className="h-20 rounded-lg" style={{ background: SAGE }} />
                  ))}
                </div>
              </div>
              {/* Gate overlay */}
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4" style={{ background: 'rgba(255,248,231,0.85)' }}>
                <div className="w-12 h-12 rounded-full flex items-center justify-center mb-4" style={{ background: PRIMARY }}>
                  <Lock size={20} className="text-white" />
                </div>
                <h3 className="font-black text-xl mb-2" style={{ color: '#111' }}>Subscribe to unlock</h3>
                <p className="text-sm mb-5" style={{ color: '#555', maxWidth: 300 }}>
                  Get full access to this week&apos;s top opportunities, insights &amp; more.
                </p>
                <button
                  className="px-8 py-3 font-black text-sm transition-all hover:opacity-90 rounded-lg"
                  style={{ background: GOLD, color: WHITE, letterSpacing: '0.02em' }}
                >
                  GET YOUR FREE ISSUE
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── SOCIAL PROOF STRIP ── */}
      <section style={{ background: SAGE, borderTop: '1px solid #c8e6c9', borderBottom: '1px solid #c8e6c9' }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-green-200">
            {[
              { icon: Heart,  label: '100% FREE.',       sub: 'Always.' },
              { icon: Users,  label: 'BUILT FOR STUDENTS.', sub: '' },
              { icon: Zap,    label: 'MADE TO HELP YOU', sub: 'GET AHEAD.' },
            ].map(({ icon: Icon, label, sub }) => (
              <div key={label} className="flex items-center gap-4 py-6 px-8">
                <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: WHITE, border: `1px solid #c8e6c9` }}>
                  <Icon size={20} style={{ color: GOLD }} />
                </div>
                <div>
                  <div className="font-black text-sm tracking-wide" style={{ color: PRIMARY }}>{label}</div>
                  {sub && <div className="font-black text-sm" style={{ color: GOLD }}>{sub}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── RECENT ISSUES ── */}
      <section className="py-16" style={{ background: WHITE }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-xs font-bold tracking-widest uppercase mb-2" style={{ color: '#999' }}>CATCH UP ANYTIME</div>
          <div className="flex items-end justify-between mb-8">
            <h2 className="text-3xl font-black" style={{ color: '#111', letterSpacing: '-0.02em' }}>Recent Issues</h2>
            <button className="flex items-center gap-1 text-sm font-semibold" style={{ color: PRIMARY }}>
              View all issues <ArrowRight size={14} />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {RECENT_ISSUES.map((issue) => (
              <div key={issue.number} className="flex gap-4">
                {/* Placeholder thumbnail */}
                <div className="flex-shrink-0 w-20 h-24 rounded overflow-hidden relative">
                  <div className="bg-gray-200 rounded-lg w-full h-full min-h-48" />
                  <div className="absolute inset-0 flex items-end p-1.5">
                    <div className="flex items-center gap-1">
                      <div className="w-4 h-4 flex items-center justify-center font-black text-white"
                        style={{ background: PRIMARY, borderRadius: '2px', fontSize: '6px' }}>OK</div>
                    </div>
                  </div>
                </div>
                {/* Content */}
                <div>
                  <div className="text-xs mb-1" style={{ color: '#888' }}>{issue.date} · ISSUE {issue.number}</div>
                  <div className="font-bold text-sm leading-snug mb-2" style={{ color: '#111' }}>{issue.headline}</div>
                  <button className="flex items-center gap-1 text-xs font-semibold" style={{ color: PRIMARY }}>
                    Read Issue <ArrowRight size={12} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CROSS-LINKS ── */}
      <section className="py-14" style={{ background: CREAM, borderTop: '1px solid #e0d8c8' }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-xs font-bold tracking-widest uppercase mb-10" style={{ color: '#999' }}>
            MORE FROM STUDENTSIGNAL
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="rounded-xl p-6" style={{ background: SAGE, border: '1px solid #c8e6c9' }}>
              <div className="font-black mb-1" style={{ color: PRIMARY }}>STUDENT DOOR</div>
              <div className="text-xs font-bold mb-3" style={{ color: PRIMARY + '80' }}>PODCAST</div>
              <p className="text-xs mb-4" style={{ color: '#555' }}>Real conversations, big insights, twice a month.</p>
              <Link to="/podcast">
                <button className="w-full py-2.5 text-xs font-black text-white rounded" style={{ background: PRIMARY }}>
                  EXPLORE STUDENT DOOR
                </button>
              </Link>
            </div>
            <div className="rounded-xl p-6" style={{ background: SAGE, border: '1px solid #c8e6c9' }}>
              <div className="font-black mb-1" style={{ color: PRIMARY }}>STUDENTSIGNAL MAGAZINE</div>
              <div className="text-xs font-bold mb-3" style={{ color: PRIMARY + '80' }}>MONTHLY ISSUE</div>
              <p className="text-xs mb-4" style={{ color: '#555' }}>In-depth stories, expert advice & campus life.</p>
              <Link to="/magazine">
                <button className="w-full py-2.5 text-xs font-black text-white rounded" style={{ background: PRIMARY }}>
                  READ THE MAGAZINE
                </button>
              </Link>
            </div>
            <div className="rounded-xl p-6" style={{ background: WARM_CARD, border: `2px solid ${GOLD}` }}>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 flex items-center justify-center font-black text-white text-xs"
                  style={{ background: PRIMARY, borderRadius: '4px' }}>OK</div>
                <span className="font-black text-sm" style={{ color: PRIMARY }}>NEWSLETTER</span>
              </div>
              <p className="text-xs mb-4" style={{ color: '#555' }}>Opportunity Knocks delivered weekly. Stay ahead.</p>
              <button className="w-full py-2.5 text-xs font-black rounded" style={{ background: GOLD, color: WHITE }}>
                YOU&apos;RE IN THE RIGHT PLACE
              </button>
            </div>
          </div>
          <div className="mt-10 flex items-center justify-between flex-wrap gap-3">
            <span className="font-black tracking-wide" style={{ color: PRIMARY }}>STUDENT<span style={{ color: GOLD }}>SIGNAL</span></span>
            <span className="text-xs" style={{ color: '#999' }}>© 2025 StudentSignal. All rights reserved.</span>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
